import { GOOGLEMAPS_ENDPOINTS } from "../../../api/network/endpoints";
import {
  GoogleSnapToRoadResponse,
  TGoogleRoutePoint,
} from "../../../api/types/Map";
import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import { VehicleStatusCode } from "../../../api/types/VehicleStatusCode";
import { APP_CONFIG } from "../../../constants/constants";
import { calculateArrivalTime, getClosestMarker } from "../util/tripUtils";
import { TRenderRouteClassParams } from "./type";
import { approxMetersBetween } from "../multiDay/geo";
import { simplifyPath } from "../multiDay/resolveRoutePath";

const GOOGLE_SNAP_TO_ROAD_BATCH_SIZE = 100;

// Snap-to-road matches a path to roads; it does not need every fix to do
// that. Consecutive fixes closer together than this carry no extra
// information for the matcher
const MIN_METERS_BETWEEN_SNAP_POINTS = 10;
// Above this the batching cost outweighs the detail, so the simplifier is
// allowed to work harder. Well under a road's width either way.
const SNAP_SIMPLIFY_TOLERANCE_METERS = 5;
const MAX_SNAP_POINTS = 600;
const MAX_SNAP_SIMPLIFY_TOLERANCE_METERS = 20;

// Interpolation is what makes a snapped route follow the road between fixes
// - without it the line between two snapped fixes is straight, and at 5s
// reporting every turn is a diagonal through the corner lot. Where it goes
// wrong is where it invents a detour: fixes far apart, or an ambiguous road,
// and the "road between them" comes back a loop round the block or down the
// wrong street. So it's on, and each stretch between two real fixes is
// checked: road geometry much longer than the straight line between those
// fixes is a detour the vehicle didn't take, and that stretch falls back to
// the straight line. A real turn is at most ~1.4x (two sides of a block);
// a driven curve a little more.
const MAX_INTERPOLATED_DETOUR_RATIO = 2;
// short hops are allowed more slack in absolute terms - a few metres of
// snapping sideways across a road can double a 20m hop on its own
const MIN_INTERPOLATED_DETOUR_SLACK_METERS = 60;

type SnappedPoint = GoogleSnapToRoadResponse["snappedPoints"][number];

const pathLengthMeters = (points: SnappedPoint[]) => {
  let total = 0;
  for (let i = 1; i < points.length; i++)
    total += approxMetersBetween(
      { lat: points[i - 1].location.latitude, lng: points[i - 1].location.longitude },
      { lat: points[i].location.latitude, lng: points[i].location.longitude }
    );
  return total;
};

// drops the interpolated points of any stretch that detours - see above.
// the snapped fixes themselves are always kept.
const withoutInterpolatedDetours = (
  snappedPoints: SnappedPoint[]
): SnappedPoint[] => {
  const kept: SnappedPoint[] = [];
  let stretch: SnappedPoint[] = [];
  const isOriginal = (point: SnappedPoint) =>
    point.originalIndex !== undefined && point.originalIndex !== null;

  snappedPoints.forEach((point) => {
    if (!isOriginal(point)) {
      stretch.push(point);
      return;
    }
    const previousOriginal = kept.length > 0 ? kept[kept.length - 1] : null;
    if (previousOriginal && stretch.length > 0) {
      const direct = pathLengthMeters([previousOriginal, point]);
      const viaRoad = pathLengthMeters([previousOriginal, ...stretch, point]);
      const allowed = Math.max(
        direct * MAX_INTERPOLATED_DETOUR_RATIO,
        direct + MIN_INTERPOLATED_DETOUR_SLACK_METERS
      );
      if (viaRoad <= allowed) kept.push(...stretch);
    }
    stretch = [];
    kept.push(point);
  });
  // interpolated points after the last fix have nothing to be checked
  // against, and nothing to lead to - drop them
  return kept;
};

// Kept conservative on purpose. Unlike the old pre-segmentation thinning,
// this runs after the hybrid has already decided snap-vs-directions, so it
// cannot change that decision - but the snapped result is still what gets
// drawn, so the shape has to survive.
const thinForSnapping = (
  points: VehicleMapDataPoint[]
): VehicleMapDataPoint[] => {
  if (points.length < 3) return points;

  const kept: VehicleMapDataPoint[] = [points[0]];
  for (let i = 1; i < points.length - 1; i++) {
    const previous = kept[kept.length - 1];
    if (
      approxMetersBetween(
        { lat: previous.latitude, lng: previous.longitude },
        { lat: points[i].latitude, lng: points[i].longitude }
      ) >= MIN_METERS_BETWEEN_SNAP_POINTS
    )
      kept.push(points[i]);
  }
  kept.push(points[points.length - 1]);

  let simplified = simplifyPath(kept, SNAP_SIMPLIFY_TOLERANCE_METERS);
  let tolerance = SNAP_SIMPLIFY_TOLERANCE_METERS;
  while (
    simplified.length > MAX_SNAP_POINTS &&
    tolerance < MAX_SNAP_SIMPLIFY_TOLERANCE_METERS
  ) {
    tolerance = Math.min(tolerance * 2, MAX_SNAP_SIMPLIFY_TOLERANCE_METERS);
    simplified = simplifyPath(kept, tolerance);
  }
  return simplified;
};

// Google snap points to road
export const gSnapPointsToRoad = ({
  tripIndex,
  tripPoints: allTripPoints,
  successCallback,
  errorCallback,
}: {
  tripIndex: number;
  tripPoints: VehicleMapDataPoint[];
  successCallback?: ({
    pointsOriginal,
    pointsInterpolated,
  }: {
    pointsOriginal: VehicleMapDataPoint[];
    pointsInterpolated: TGoogleRoutePoint[];
  }) => void;
  errorCallback?: (points: VehicleMapDataPoint[]) => void;
}) => {
  const batchSize = GOOGLE_SNAP_TO_ROAD_BATCH_SIZE;
  // Reduce the fixes before batching. A parked asset reports the same
  // position dozens of times and a straight run needs two points, not two
  // hundred - none of it tells the road matcher anything, and every 100
  // fixes is another request.
  //
  // Deliberately here rather than further up: the DEFAULT hybrid decides
  // snap-vs-directions from how far apart the fixes are, so thinning before
  // that made dense trips look sparse and sent them to Directions, which
  // fabricates a route instead of snapping what it was given.
  const tripPoints = thinForSnapping(allTripPoints);
  // break the waypoints into batches that share their boundary point, so
  // each seam is snapped (and interpolated) as part of a batch rather than
  // left as a straight jump from one batch's last fix to the next's first
  const batches: VehicleMapDataPoint[][] = [];
  const batchStarts: number[] = [];
  for (let i = 0; i < tripPoints.length - 1 || i === 0; i += batchSize - 1) {
    batchStarts.push(i);
    batches.push(tripPoints.slice(i, Math.min(i + batchSize, tripPoints.length)));
    if (i + batchSize >= tripPoints.length) break;
  }
  if (APP_CONFIG.DEBUG.MAPS_GSNAPPOINTSTOROAD)
    console.log(
      `========= TRIP#${tripIndex} BATCHES [gSnapPointsToRoad]`,
      batches
    );
  // create API call promises for each batch
  const batchPromises = batches.map((batch, batchIndex) => {
    const url = GOOGLEMAPS_ENDPOINTS.SNAP_TO_ROAD(
      batch.map((waypoint) => ({
        lat: waypoint.latitude, // (waypoint?.location as google.maps.LatLng)?.lat(),
        lng: waypoint.longitude, // (waypoint?.location as google.maps.LatLng)?.lng(),
      })),
      // interpolation on, with its detours filtered out below - see
      // withoutInterpolatedDetours
      true
    );
    if (APP_CONFIG.DEBUG.MAPS_GSNAPPOINTSTOROAD) {
      console.log(
        `========= TRIP#${tripIndex} BATCH REQ #${batchIndex} [gSnapPointsToRoad]`,
        batch.map((waypoint) => ({
          lat: waypoint.latitude, // (waypoint?.location as google.maps.LatLng)?.lat(),
          lng: waypoint.longitude, // (waypoint?.location as google.maps.LatLng)?.lng(),
        }))
      );
      console.log(
        `========= TRIP#${tripIndex} BATCH URL #${batchIndex} [gSnapPointsToRoad]`,
        url
      );
    }
    // "default" caching, not "force-cache". force-cache reuses ANY stored
    // response regardless of freshness, so a transient Google error (quota,
    // 5xx) returned once could be served from cache for the rest of the
    // session, poisoning every later view of the same trip. "default" lets
    // successful, cacheable responses still be reused per the server's
    // headers while letting error responses expire normally. The status
    // guard below ensures a non-OK response never gets parsed as snapped
    // data - it resolves to an empty-shaped result the caller already
    // tolerates (it guards on response?.snappedPoints).
    return fetch(url, { cache: "default" }).then((response) => {
      if (!response.ok) {
        if (APP_CONFIG.DEBUG.MAPS_GSNAPPOINTSTOROAD)
          console.warn(
            `========= TRIP#${tripIndex} BATCH #${batchIndex} HTTP ${response.status} [gSnapPointsToRoad] - treating as no snapped points`
          );
        return { snappedPoints: [] } as GoogleSnapToRoadResponse;
      }
      return response.json();
    });
  });
  Promise.all(batchPromises)
    .then((responses) => {
      if (APP_CONFIG.DEBUG.MAPS_GSNAPPOINTSTOROAD)
        console.log(
          `ALL TRIP#${tripIndex} BATCH RESPONSES DONE [gSnapPointsToRoad]`,
          responses
        );
      const interpolatedTripPoints: TGoogleRoutePoint[] = [];
      responses.forEach((response, batchIndex) => {
        if (APP_CONFIG.DEBUG.MAPS_GSNAPPOINTSTOROAD) {
          console.log(
            `======== TRIP#${tripIndex} BATCH RESP #${batchIndex} [gSnapPointsToRoad]`,
            response
          );
        }
        if (response?.snappedPoints) {
          withoutInterpolatedDetours(
            (response as GoogleSnapToRoadResponse).snappedPoints
          ).forEach((snappedPoint) => {
            // `!== undefined` rather than truthiness - index 0 is a real
            // fix, and was being treated as an interpolated one
            const isOriginal =
              snappedPoint.originalIndex !== undefined &&
              snappedPoint.originalIndex !== null;
            // a later batch's first fix is the previous batch's last one
            if (isOriginal && batchIndex > 0 && Number(snappedPoint.originalIndex) === 0)
              return;
            interpolatedTripPoints.push({
              ...snappedPoint,
              ...(isOriginal
                ? {
                    originalIndex:
                      batchStarts[batchIndex] + Number(snappedPoint.originalIndex),
                    tripPoint:
                      batches[batchIndex][Number(snappedPoint.originalIndex)],
                  }
                : // interpolated points do not have originalIndex
                  { originalIndex: undefined }),
            });
          });
        }
      });
      if (APP_CONFIG.DEBUG.MAPS_GSNAPPOINTSTOROAD)
        console.log(
          `======== TRIP#${tripIndex} BATCH INTERPOLATED [gSnapPointsToRoad]`,
          interpolatedTripPoints
        );
      successCallback?.({
        pointsOriginal: tripPoints,
        pointsInterpolated: interpolatedTripPoints,
      });
      // handle the responses here if needed
      // const snappedTripPoints = tripPoints.map((point, tripPointIndex) => {
      //   const snappedPoint =
      //     responses?.[0]?.snappedPoints?.find(
      //       (_snappedPoint) => _snappedPoint.originalIndex === tripPointIndex
      //     );
      //   if (!snappedPoint) return point;
      //   if (APP_CONFIG.DEBUG.MAPS)
      //     console.log(
      //       "SNAPPED",
      //       `{lat:${point.latitude},lng:${point.longitude}}`,
      //       "TO",
      //       `{lat:${snappedPoint.location.latitude},lng:${snappedPoint.location.longitude}}`
      //     );
      //   return {
      //     ...point,
      //     latitude: snappedPoint.location.latitude,
      //     longitude: snappedPoint.location.longitude,
      //   };
      // });
      // successCallback?.(snappedTripPoints);
    })
    .catch((error) => {
      console.error("Error fetching batch data:", error);
      errorCallback?.(tripPoints);
    });
};

const MAX_VIAPOINTS = 10;
export const getGoogleRoutePointsFromTripPoints = ({
  tripPoints,
}: {
  tripPoints: VehicleMapDataPoint[];
}) => {
  const debug = false;
  let waypointCount = 0;
  let viapointCount = 0;
  const gRoutePoints: TGoogleRoutePoint[] = []; // google.maps.DirectionsWaypoint[]

  // add waypoints
  tripPoints.map((tripPoint, pointIndex) => {
    const isStart = pointIndex === 0;
    const isEnd = pointIndex === tripPoints.length - 1; // && tripPoint.statuscode === VehicleStatusCode.STATUS_IGNITION_OFF; // disabled to let the red marker show on the last point on trips list map screen
    const isMotionEvent =
      tripPoint.statuscode === VehicleStatusCode.STATUS_MOTION_HEADING ||
      tripPoint.statuscode === VehicleStatusCode.STATUS_MOTION_MOVING ||
      tripPoint.statuscode === VehicleStatusCode.STATUS_MOTION_ENROUTE ||
      tripPoint.statuscode === VehicleStatusCode.STATUS_DISTANCE ||
      tripPoint.statuscode === VehicleStatusCode.STATUS_DISTANCE_1 ||
      tripPoint.statuscode === VehicleStatusCode.STATUS_DISTANCE_2;
    // || tripPoint.statuscode === VehicleStatusCode.STATUS_MOTION_EN_ROUTE // typescript giving unintentional comparison error here probably because STATUS_MOTION_ENROUTE is equal to STATUS_MOTION_EN_ROUTE
    // limit the number of via points to 10 between two waypoints
    let isViaPoint = !isStart && !isEnd;
    if (!isStart && !isEnd) {
      if (viapointCount < MAX_VIAPOINTS) {
        isViaPoint = true;
        viapointCount++;
      } else {
        isViaPoint = false;
        waypointCount++;
        viapointCount = 0;
      }
    }
    if (pointIndex === tripPoints.length - 1) isViaPoint = false; // last point cannot be a viapoint

    // create a waypoint
    if (APP_CONFIG.DEBUG.MAPS && debug)
      console.log(`>> ${isViaPoint ? "Via" : "Way ====="}`);
    // directionsManager.addWaypoint(newWaypoint);
    gRoutePoints.push({
      location: new google.maps.LatLng(
        tripPoint.latitude,
        tripPoint.longitude
      ) as any, // strangely typescript is giving error if I directly typecast the location as google.maps.LatLng, so added 'as any'
      stopover: isViaPoint,
      tripPoint,
    });
    return tripPoint;
    // return newWaypoint;
  });

  return gRoutePoints;
};

// `suppressed` covers timing: snap-to-road and directions make real API
// calls first, so a render started in single-day mode can land well after
// the switch to multi-day. Suppressed polylines are still created and
// returned as before, they just never attach to the map.
const legacyRoutePolylines: any[] = [];
let legacyRouteRenderingSuppressed = false;

export const setLegacyRouteRenderingSuppressed = (suppressed: boolean) => {
  legacyRouteRenderingSuppressed = suppressed;
};

export const clearLegacyRoutePolylines = () => {
  legacyRoutePolylines.forEach((polyline) => polyline?.setMap?.(null));
  legacyRoutePolylines.length = 0;
};

export const renderRouteWithPolylines = ({
  props,
  tripIndex,
  polylineConfig,

  gRoutePoints,
  routeVisibility,
}: Omit<TRenderRouteClassParams, "sanitizedTripPoints"> & {
  gRoutePoints: TGoogleRoutePoint[];
  routeVisibility: boolean;
}) => {
  // create polyline manually
  const gTripPolyline = new google.maps.Polyline(polylineConfig);
  legacyRoutePolylines.push(gTripPolyline);
  gTripPolyline.setPath(
    gRoutePoints.map((waypoint) => waypoint.location as any)
  ); // "as any" added to avoid typescript error, because google.maps.LatLng is not being recognized as type of location in TGoogleRoutePoint for some reason
  gTripPolyline.setMap(
    routeVisibility && !legacyRouteRenderingSuppressed
      ? props.mapRef.current.map
      : null
  );
  // if (props.mapRef.current?.objects.mTripLayers) {
  //   props.mapRef.current.objects.mTripLayers[tripIndex] = [
  //     ...(props.mapRef.current.objects.mTripLayers[tripIndex] || []),
  //     gTripPolyline,
  //   ];
  // }
  gTripPolyline.addListener("click", (e: any) => {
    var latlng = e?.latLng;
    if (!latlng?.lat?.() || !latlng?.lng?.()) return;
    // find the closest marker to the
    const marker = getClosestMarker(
      { lat: latlng.lat(), lng: latlng.lng() },
      gRoutePoints
    );
    if (marker) {
      const estimatedPointTime = calculateArrivalTime(
        latlng.lat(),
        latlng.lng(),
        marker,
        gRoutePoints[gRoutePoints.length - 1]
      );
      console.log("estimated", estimatedPointTime);
      props &&
        props?.onRoutePointClick &&
        props?.onRoutePointClick(estimatedPointTime ?? 0);
    }
  });

  // set polyline as mapElements for this route view, so that it can be shown/hidden using show/hide methods of the class
  // this.setMapElements([gTripPolyline]);

  // return polyline so that it can be set as mapElements for this route view,
  // so that it can be shown/hidden using show/hide methods of the class
  return [gTripPolyline];
};

// line symbol for dashed line
export const lineSymbolForDashedLine = {
  path: "M 0,-1 0,1",
  strokeOpacity: 1,
  scale: 2, // 4
};

export const lineSymbolForDottedLine = {
  path: "M 0,0 0,0",
  strokeOpacity: 1,
  scale: 4,
};

const polylineConfigLineStylesCommon = {
  strokeColor: "#000000", // gets replaced by the actual color
  strokeOpacity: 1,
  strokeWeight: 3,
};
export const polylineConfigLineStyles = {
  DASHED: {
    ...polylineConfigLineStylesCommon,
    strokeOpacity: 0, // Hide the main line
    icons: [
      {
        icon: lineSymbolForDashedLine,
        offset: "0",
        repeat: "10px", // Distance between dashes
      },
    ],
  },
  DOTTED: {
    ...polylineConfigLineStylesCommon,
    strokeOpacity: 0, // Hide the main line
    icons: [
      {
        icon: lineSymbolForDottedLine,
        offset: "0",
        repeat: "5px", // Distance between dashes
      },
    ],
  },
  SOLID_THICK: {
    ...polylineConfigLineStylesCommon,
    strokeWeight: 5,
  },
  SOLID_THIN: {
    ...polylineConfigLineStylesCommon,
  },
};
